import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { getAskRovoClickedEvent } from '../askRovoAnalytics.ts';

// The same full-width bordered pill as the footer's Share context button, so the two Rovo
// entries in the panel read as one family.
// Button has fixed vertical padding, so Pressable.
const styles = cssMap({
	button: {
		display: 'flex',
		width: '100%',
		height: '32px',
		paddingBlock: token('space.0'),
		paddingInline: token('space.150'),
		justifyContent: 'center',
		alignItems: 'center',
		gap: token('space.075'),
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		font: token('font.body'),
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
});

const i18n = defineMessages({
	chatWithRovo: {
		id: 'byline-connections.preview-panel.at-a-glance.chat-with-rovo-button',
		defaultMessage: 'Chat with Rovo',
		description:
			'Label for the full-width button at the end of the "Context from your work" section of the Teamwork Graph preview panel and modal that opens Rovo chat to ask questions about this page and the work connected to it.',
	},
});

export type TeamworkGraphAskRovoButtonProps = {
	onClick: () => void;
	testId?: string;
};

/**
 * The "Chat with Rovo" action, rendered at the end of the "Context from your work" section in both
 * the preview panel and the modal. The button carries no prompt of its own — its `onClick`
 * decides what, if anything, seeds the chat (both surfaces seed it with the connection links).
 * One control, no chevron: the dropdown of Rovo actions it once promised was never designed.
 */
export function TeamworkGraphAskRovoButton({
	onClick,
	testId,
}: TeamworkGraphAskRovoButtonProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(() => {
		createAnalyticsEvent(getAskRovoClickedEvent({ trigger: 'atAGlance' })).fire();
		onClick();
	}, [createAnalyticsEvent, onClick]);

	return (
		<Pressable xcss={styles.button} onClick={handleClick} testId={testId}>
			<RovoHexIcon label="" size="xxsmall" />
			<Text color="color.text.subtle" size="medium" weight="medium" maxLines={1}>
				{formatMessage(i18n.chatWithRovo)}
			</Text>
		</Pressable>
	);
}
